import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import { ErrorComponent, FormContainer, FullLoader } from '@openx/components/core';
import type { Shape } from '@openx/types';
import type { User } from 'firebase/auth';
import has from 'lodash/has';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router';
import { boolean, type ObjectSchema, object, string } from 'yup';

import { getEnvironmentConfigs } from '../../../environmentConfigs';
import { useAuth } from '../components/AuthContext';
import { InstanceSelect } from '../components/InstanceSelect';
import { SsoLayout } from '../components/SsoLayout';
import { BACK_TO_STORAGE_KEY } from '../constants';
import type { InstanceListOption } from '../types';
import { fetchConsentResponse } from '../utils/fetchConsentResponse';
import { getDefaultInstance } from '../utils/getDefaultInstance';
import { useFetchSessionInfo } from '../utils/useFetchSessionInfo';
import { useLogout } from '../utils/useLogout';
import { useOxsTerms } from '../utils/useOxsTerms';
import { getOAuthParams, getOAuthRedirectURL } from '../utils/verifyOAuthParams';

import { ApplicationInfo } from './ApplicationInfo';
import { OxsTermsField } from './OxsTermsField';

interface FormValues {
  instance: InstanceListOption | null;
  sessionId: string | null;
  scopes: { [key: string]: boolean };
  termsAccepted: boolean;
}

export const REQUIRED_SCOPES = new Set(['openid']);

const validationSchema = object({
  instance: object<null, Shape<InstanceListOption>>()
    .nullable()
    .required('Please select an instance')
    .test({
      message: 'Please select an instance',
      name: 'instance',
      test(value) {
        return !(!value || !has(value, 'id'));
      },
    }),
  scopes: object().test({
    message: 'Scopes should be set if session exists',
    name: 'scopes',
    test(value, ctx) {
      if (!ctx.parent.sessionId) {
        return true;
      }

      for (const requiredScope of REQUIRED_SCOPES.keys()) {
        if (!value[requiredScope]) {
          return ctx.createError({
            message: `Required scope '${requiredScope}' is missing`,
          });
        }
      }
      return true;
    },
  }),
  sessionId: string().min(1).nullable().required(),
  // `$showTerms` comes from the form context; there is no error text because Authorize just stays disabled
  termsAccepted: boolean()
    .required()
    .when('$showTerms', {
      is: true,
      then: schema => schema.oneOf([true]),
    }),
}) as ObjectSchema<FormValues>;

const docsRegexp = /^(?:https?:\/\/)?(?:[a-z0-9-]*docs[a-z0-9-]*(?:\.[a-z0-9-]+)*)\.openx\.(?:com|net|org)(?:\/.*)?$/i;

export function Consent() {
  const { dcrDeveloperEmail, requireConsent, unityOrigin } = getEnvironmentConfigs();

  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const { sessionId } = getOAuthParams(location);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const { loading, registered, error, instancesList, session } = useFetchSessionInfo();
  const [isLoading, setIsLoading] = useState<boolean>(loading);

  // AI connectors (MCP clients) are registered via Apigee DCR under a dedicated developer.
  const isAiConnector = !!dcrDeveloperEmail && session?.developer_email === dcrDeveloperEmail;
  // DCR clients choose their own redirect URI, so an AI connector never gets the docs auto-consent (it skips the T&C)
  const cameFromDocs = useMemo(
    () => !isAiConnector && docsRegexp.test(session?.redirect_uri || ''),
    [isAiConnector, session]
  );
  const oxsTerms = useOxsTerms(isAiConnector);
  const { showTerms } = oxsTerms;

  const toggleLoading = useCallback(() => {
    setIsLoading(prev => !prev);
  }, []);

  const { logout } = useLogout();

  const handleLogout = useCallback(async () => {
    toggleLoading();
    await logout();
    toggleLoading();
  }, [logout, toggleLoading]);

  const formMethods = useForm<FormValues>({
    context: { showTerms },
    defaultValues: {
      instance: null,
      scopes: {},
      sessionId: null,
      termsAccepted: false,
    },
    resolver: yupResolver<FormValues>(validationSchema),
  });

  const onNoSessionLogin = useCallback(
    (values: { instance: InstanceListOption }) => {
      setIsDisabled(true);
      try {
        if (!values.instance) {
          enqueueSnackbar('No instance selected', { variant: 'error' });
          return;
        }

        const user = currentUser as User;
        const email = user.email as string;

        const url = new URL(unityOrigin || values.instance.urls[0]);

        url.pathname = '/login';
        url.searchParams.set('email', email);

        window.location.href = url.toString();
      } catch (error) {
        enqueueSnackbar(`Error: "${error.message}". Try reloading the page`, {
          variant: 'error',
        });
      } finally {
        setIsDisabled(false);
      }
    },
    [currentUser, enqueueSnackbar, unityOrigin]
  );

  const onConsent = useCallback(
    async (values: FormValues): Promise<void> => {
      setIsDisabled(true);
      // keep the form disabled while the browser navigates away
      let redirecting = false;

      try {
        if (!values.instance) {
          enqueueSnackbar('No instance selected', { variant: 'error' });
          return;
        }
        // Accept first, so a failed write never consents; skip it when already accepted to keep the original date.
        if (showTerms) {
          await oxsTerms.accept();
        }
        const response = await fetchConsentResponse({
          currentUser: currentUser as User,
          formParams: {
            consent: true,
            instanceUID: values.instance.id,
            scope: Object.entries(values.scopes)
              .filter(([_scope, enabled]) => enabled)
              .map(([scope]) => scope)
              .join(' '),
            sessionId,
          },
        });

        if (response.redirect_uri) {
          const backTo = sessionStorage.getItem(BACK_TO_STORAGE_KEY);
          const redirectUri = new URL(response.redirect_uri);

          redirectUri.searchParams.set('instance_name', values.instance.label);

          if (backTo) {
            redirectUri.searchParams.set('back_to', backTo);
          }

          redirecting = true;
          window.location.href = getOAuthRedirectURL({ ...response, redirect_uri: redirectUri.toString() });
          return;
        } else {
          enqueueSnackbar('No redirect URI provided in the response.', { variant: 'warning' });
        }
      } catch (error) {
        enqueueSnackbar(`Error: "${error.message}". Try reloading the page`, {
          variant: 'error',
        });
      } finally {
        if (!redirecting) {
          setIsDisabled(false);
        }
      }
    },
    [currentUser, enqueueSnackbar, sessionId, showTerms, oxsTerms.accept]
  );

  const onDissent = useCallback(async () => {
    setIsDisabled(true);
    try {
      const response = await fetchConsentResponse({
        currentUser: currentUser as User,
        formParams: {
          consent: false,
          sessionId,
        },
      });

      window.location.href = getOAuthRedirectURL(response);
    } catch (error) {
      setIsDisabled(false);
      enqueueSnackbar(`Error: "${error.message}". Try reloading the page`, {
        variant: 'error',
      });
    }
  }, [currentUser, sessionId, enqueueSnackbar]);

  const onSubmit: SubmitHandler<FormValues> = async data => {
    if (!data.instance) {
      return;
    }
    if (!data.sessionId) {
      onNoSessionLogin(data as { instance: InstanceListOption });
      return;
    }

    await onConsent(data);
  };

  const resetFormValues = useCallback(
    (instance: InstanceListOption | null) => {
      formMethods.reset({
        instance,
        scopes: session?.scope.split(' ').reduce<{
          [key: string]: boolean;
        }>((acc, scope) => ({ ...acc, [scope]: true }), {}),
        sessionId,
        termsAccepted: oxsTerms.terms?.accepted ?? false,
      });
    },
    [session?.scope, formMethods, sessionId, oxsTerms.terms?.accepted]
  );

  const handleConsentAndRedirectToDocs = useCallback(async () => {
    try {
      const values = formMethods.getValues();

      setIsDisabled(true);
      setIsLoading(true);

      const response = await fetchConsentResponse({
        currentUser: currentUser as User,
        formParams: {
          consent: true,
          instanceUID: instancesList?.[0]?.id ?? '',
          scope: Object.entries(values.scopes)
            .filter(([_scope, enabled]) => enabled)
            .map(([scope, _enabled]) => scope)
            .join(' '),
          sessionId,
        },
      });

      if (response.redirect_uri) {
        window.location.href = getOAuthRedirectURL(response);
        return;
      }

      setIsDisabled(false);
      setIsLoading(false);
    } catch (error) {
      setIsDisabled(false);
      setIsLoading(false);
      enqueueSnackbar(`Error: "${error.message}". Try reloading the page`, {
        variant: 'error',
      });
    }
  }, [currentUser, sessionId, formMethods.getValues, enqueueSnackbar, instancesList]);

  const onDocsButtonClick = useCallback(
    async (docsUrl: string) => {
      if (cameFromDocs) {
        resetFormValues(null);
        handleConsentAndRedirectToDocs();
      } else {
        window.location.href = docsUrl;
      }
    },
    [cameFromDocs, resetFormValues, handleConsentAndRedirectToDocs]
  );

  // handling consent and redirection if user came from docs
  useEffect(() => {
    if (cameFromDocs && session && instancesList?.length) {
      resetFormValues(null);

      handleConsentAndRedirectToDocs();
    }
  }, [cameFromDocs, handleConsentAndRedirectToDocs, resetFormValues, session, instancesList]);

  // The default instance is applied only once, when the data is first ready. instancesList/session get new
  // references whenever the session queries run again, and resetting the form then would discard the instance
  // the user has already picked or is typing to filter the list.
  const isFormInitialized = useRef(false);

  // A T&C lookup that is retried or refetched after the form was initialized changes `showTerms` (the validation
  // context) and the pre-checked state, so re-sync the checkbox and re-validate.
  useEffect(() => {
    if (isFormInitialized.current) {
      formMethods.setValue('termsAccepted', oxsTerms.terms?.accepted ?? false, { shouldValidate: true });
    }
  }, [oxsTerms.terms, formMethods]);

  useEffect(() => {
    if (loading || !session || oxsTerms.isPending || isFormInitialized.current) {
      return;
    }

    isFormInitialized.current = true;
    resetFormValues(getDefaultInstance(instancesList));

    // when coming from docs, consent + redirect is already in progress (see effect above) - keep the loader
    if (!cameFromDocs) {
      setIsLoading(false);
    }
  }, [resetFormValues, instancesList, session, loading, oxsTerms.isPending, cameFromDocs]);

  useEffect(() => {
    if (!error) {
      return;
    }

    if (error.type === 'authorization' || error.message === 'Unauthenticated') {
      handleLogout();
    } else {
      enqueueSnackbar(error.message, {
        variant: 'error',
      });
    }
  }, [error, enqueueSnackbar, handleLogout]);

  if ((isLoading || oxsTerms.isPending) && !error) {
    return <FullLoader />;
  }

  if (!currentUser) {
    return <Navigate to={`/login${location.search}${location.hash}`} />;
  }

  if (!loading && (!registered || !instancesList || !instancesList.length)) {
    return (
      <ErrorComponent
        title="Not registered"
        titlePrefix="OpenX"
        subtitle="Something went wrong or you are not registered"
        actions={[
          {
            children: 'Contact support',
            variant: 'contained',
          },
          {
            children: 'Logout',
            variant: 'text',
            onClick: handleLogout,
          },
        ]}
      ></ErrorComponent>
    );
  }

  return (
    <SsoLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} data-test="consent-form">
          <FormContainer fullPage>
            <InstanceSelect instancesList={instancesList || []} onButtonClick={onDocsButtonClick} />

            {session && requireConsent && (
              <>
                <ApplicationInfo
                  appName={session.app_name}
                  clientId={session.client_id}
                  developerEmail={session.developer_email}
                ></ApplicationInfo>

                <Typography variant="h3" gutterBottom data-test="scopes-text">
                  Scopes
                </Typography>
                <FormGroup>
                  {session.scope.split(' ').map(scope => (
                    <FormControlLabel
                      key={scope}
                      label={scope}
                      data-test={scope}
                      control={
                        <Controller
                          name={`scopes.${scope}`}
                          control={formMethods.control}
                          render={({ field }) => (
                            <>
                              <Checkbox defaultChecked disabled={REQUIRED_SCOPES.has(scope)} {...field}></Checkbox>
                            </>
                          )}
                        ></Controller>
                      }
                    ></FormControlLabel>
                  ))}
                </FormGroup>
              </>
            )}

            {showTerms && (
              <OxsTermsField
                isAccepted={oxsTerms.terms?.accepted === true}
                isError={oxsTerms.isError}
                isRetrying={oxsTerms.isFetching}
                onRetry={oxsTerms.retry}
              />
            )}
          </FormContainer>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}
          >
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Button
                type="button"
                variant="text"
                data-test="logout-button"
                onClick={handleLogout}
                disabled={isDisabled}
              >
                Log out
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1 }}> </Box>
            {requireConsent && session && (
              <Box sx={{ alignSelf: 'flex-end' }}>
                <Button type="button" variant="text" data-test="deny-button" onClick={onDissent} disabled={isDisabled}>
                  Deny
                </Button>
              </Box>
            )}
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={!formMethods.formState.isValid || formMethods.formState.isSubmitting || isDisabled}
                data-test="authorize-button"
              >
                Authorize instance
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </SsoLayout>
  );
}
